import type { CatalogLengthOption } from '~/api/productLengths';
import type { CatalogProduct } from '~/components/common/CatalogProductSearch';
import {
  hasInvalidBundleCount,
  positiveBundleCounts,
  type TallyBundleCount,
} from '~/components/common/tallyBundleCounts';
import type {
  PlatformProductCannedTalliesQuery,
  Quantity,
  UnitCode,
} from '~/graphql/graphql';

type CatalogProductLength = CatalogLengthOption['platformProductLength'];

export type CannedTallyOption =
  PlatformProductCannedTalliesQuery['platformProductCannedTallies'][number];

export type PickedProductLength = {
  platformProductsLength: CatalogProductLength;
  standardUnitsCode: UnitCode;
  quantity: Quantity;
};

export type CompleteLengthPick = {
  kind: 'length';
  isComplete: true;
} & PickedProductLength;

export type LengthPick =
  | CompleteLengthPick
  | {
      kind: 'length';
      isComplete: false;
      platformProductsLength: CatalogProductLength | null;
      standardUnitsCode: UnitCode | null;
      quantity: Quantity | null;
    };

export type CannedTallyPick = {
  kind: 'canned';
  isComplete: boolean;
  platformProduct: CatalogProduct;
  cannedTally: CannedTallyOption;
  tallyCount: number;
};

export type RandomTallyPick = {
  kind: 'random';
  isComplete: boolean;
  platformProduct: CatalogProduct;
  bundles: TallyBundleCount[];
};

export type LengthsPick = {
  kind: 'lengths';
  isComplete: boolean;
  lines: PickedProductLength[];
};

export type ProductLengthPick =
  LengthPick | LengthsPick | CannedTallyPick | RandomTallyPick;

export type InitialProductLengthPick = {
  product: CatalogProduct;
  length: CatalogLengthOption;
  quantity: Quantity | null;
  piecesPerBundle?: number;
};

export type LengthLine = {
  length: CatalogLengthOption;
  quantity: Quantity | null;
  piecesPerBundle?: number;
};

export type ProductLengthChoice =
  | { kind: 'none' }
  | { kind: 'lengths'; lines: LengthLine[] }
  | ({ kind: 'length' } & LengthLine)
  | {
      kind: 'canned';
      cannedTally: CannedTallyOption;
      tallyCount: number;
      transportVehicleId: string | null;
    }
  | { kind: 'random'; bundleCountsByLengthId: Record<string, string> };

export type ProductLengthPickContext = {
  onlyLength: CatalogLengthOption | undefined;
  candidateLengthIds: readonly string[];
  preferredUnit: UnitCode;
};

export const NO_CHOICE: ProductLengthChoice = { kind: 'none' };

function hasPositiveQuantity(quantity: Quantity | null): quantity is Quantity {
  return !!quantity && quantity.value > 0;
}

export function initialChoiceOf(
  initialPick: InitialProductLengthPick
): ProductLengthChoice {
  return {
    kind: 'length',
    length: initialPick.length,
    quantity: initialPick.quantity,
    piecesPerBundle: initialPick.piecesPerBundle,
  };
}

export function isInitialPickComplete(
  initialPick: InitialProductLengthPick
): boolean {
  return pickOf(initialPick.product, initialChoiceOf(initialPick), {
    onlyLength: undefined,
    candidateLengthIds: [],
    preferredUnit: initialPick.product.standardUnitsCode,
  }).isComplete;
}

export function lengthOfChoice(
  choice: ProductLengthChoice,
  onlyLength: CatalogLengthOption | undefined
): CatalogLengthOption | null {
  if (choice.kind === 'length') return choice.length;
  if (choice.kind === 'none') return onlyLength ?? null;
  return null;
}

export function quantityOfChoice(
  choice: ProductLengthChoice,
  hasLength: boolean,
  preferredUnit: UnitCode
): Quantity | null {
  const enteredQuantity = choice.kind === 'length' ? choice.quantity : null;
  return (
    enteredQuantity ?? (hasLength ? { value: 0, unit: preferredUnit } : null)
  );
}

export function pickOf(
  product: CatalogProduct | null,
  choice: ProductLengthChoice,
  context: ProductLengthPickContext
): ProductLengthPick {
  if (product && choice.kind === 'canned') {
    return {
      kind: 'canned',
      isComplete: choice.tallyCount >= 1,
      platformProduct: product,
      cannedTally: choice.cannedTally,
      tallyCount: choice.tallyCount,
    };
  }
  if (product && choice.kind === 'lengths') {
    const lines = choice.lines.flatMap(({ length, quantity }) =>
      hasPositiveQuantity(quantity)
        ? [
            {
              platformProductsLength: length.platformProductLength,
              standardUnitsCode: product.standardUnitsCode,
              quantity,
            },
          ]
        : []
    );
    return { kind: 'lengths', isComplete: lines.length > 0, lines };
  }
  if (product && choice.kind === 'random') {
    const { bundleCountsByLengthId } = choice;
    const bundles = positiveBundleCounts(
      bundleCountsByLengthId,
      context.candidateLengthIds
    );
    return {
      kind: 'random',
      isComplete:
        bundles.length > 0 &&
        !hasInvalidBundleCount(
          bundleCountsByLengthId,
          context.candidateLengthIds
        ),
      platformProduct: product,
      bundles,
    };
  }
  const length = lengthOfChoice(choice, context.onlyLength);
  const platformProductsLength = length && length.platformProductLength;
  const standardUnitsCode = product ? product.standardUnitsCode : null;
  const quantity = quantityOfChoice(
    choice,
    !!platformProductsLength,
    context.preferredUnit
  );
  return standardUnitsCode &&
    platformProductsLength &&
    hasPositiveQuantity(quantity)
    ? {
        kind: 'length',
        isComplete: true,
        platformProductsLength,
        standardUnitsCode,
        quantity,
      }
    : {
        kind: 'length',
        isComplete: false,
        platformProductsLength,
        standardUnitsCode,
        quantity,
      };
}

type PickSaves = {
  length: (pick: CompleteLengthPick) => void;
  canned: (pick: CannedTallyPick) => void;
  random: (pick: RandomTallyPick) => void;
  lengths: (pick: LengthsPick) => void;
};

/**
 * Runs the save that matches the kind of `pick`, and nothing while it is incomplete.
 * Only a row made with `pickSeveralLengths` makes a `lengths` pick, so another row's
 * `lengths` save is never called.
 *
 * @example
 *   saveCompletePick(picked, {
 *     length: (lengthPick) => addProduct(lengthPick),
 *     canned: (cannedPick) => addCannedTally(cannedPick),
 *     random: (randomPick) => setRandomTally(randomPick),
 *     lengths: (lengthsPick) => addEachLength(lengthsPick.lines),
 *   });
 */
export function saveCompletePick(pick: ProductLengthPick, saves: PickSaves) {
  if (!pick.isComplete) return;
  if (pick.kind === 'canned') saves.canned(pick);
  else if (pick.kind === 'random') saves.random(pick);
  else if (pick.kind === 'lengths') saves.lengths(pick);
  else saves.length(pick);
}
